// frontend/src/pages/MatchScoring/ExtrasModal.js
//
// One component handles all four extras (Wide, No-ball, Bye, Leg-bye) by
// switching internal sections based on `kind`. On confirm, returns a
// "delivery" object the parent can build a /balls payload from:
//
//   {
//     kind: 'wd' | 'nb' | 'b' | 'lb',
//     // For wide: total runs (incl. the 1 wide penalty); underlying type
//     // For no-ball: source (none/bat/bye/legbye) + extra runs (off the bat or extras)
//     // For bye/leg-bye: just runs
//     ...,
//     wicketKind: null | 'stumped' | 'runout',     // optional inline wicket signal
//   }
//
// The parent constructs the ball_events payload from this object — that
// keeps mapping logic in one place (index.js).

import React, { useEffect, useState } from 'react';

export default function ExtrasModal({ kind, onCancel, onConfirm }) {
  const [runs, setRuns]               = useState(1);
  const [wideUnderlying, setWUnder]   = useState('extra');     // 'extra' | 'bye' | 'legbye'
  const [nbSource, setNbSource]       = useState('none');      // 'none' | 'bat' | 'bye' | 'legbye'
  const [nbExtraRuns, setNbExtraRuns] = useState(1);           // additional runs beyond the no-ball penalty
  const [wicketKind, setWicketKind]   = useState('no');        // 'no' | 'stumped' | 'runout'

  // Reset on mount/kind change
  useEffect(() => {
    setRuns(1); setWUnder('extra');
    setNbSource('none'); setNbExtraRuns(1);
    setWicketKind('no');
  }, [kind]);

  const showWideUnderlying = kind === 'wd' && runs >= 2;
  const allowedWicketsByKind = {
    wd: ['no', 'stumped', 'runout'],
    nb: ['no', 'runout'],          // can't bowl/lbw on no-ball; stumping voided
    b:  ['no', 'stumped', 'runout'],
    lb: ['no', 'runout'],
  }[kind];

  function summary() {
    if (kind === 'wd') {
      const total = runs;
      let underTag = '';
      if (runs >= 2) {
        if (wideUnderlying === 'bye')    underTag = ' · underlying byes';
        else if (wideUnderlying === 'legbye') underTag = ' · underlying leg-byes';
        else                            underTag = ' · underlying extra running';
      }
      return `Total ${total}r${underTag}${wicketKind !== 'no' ? ' + wicket' : ''}`;
    }
    if (kind === 'nb') {
      const r = nbSource === 'none' ? 0 : nbExtraRuns;
      const total = 1 + r;
      const lbl = nbSource === 'bat'    ? `+${r} off bat`
                : nbSource === 'bye'    ? `+${r} byes`
                : nbSource === 'legbye' ? `+${r} leg-byes`
                : '';
      return `Total ${total}r ${lbl}${wicketKind !== 'no' ? ' + wicket' : ''} · free-hit next`;
    }
    if (kind === 'b' || kind === 'lb') {
      const word = kind === 'b' ? 'bye' : 'leg-bye';
      return `Total ${runs}r as ${word}${runs === 1 ? '' : 's'}${wicketKind !== 'no' ? ' + wicket' : ''}`;
    }
    return '';
  }

  function confirm() {
    const out = { kind, wicketKind: wicketKind === 'no' ? null : wicketKind };
    if (kind === 'wd') {
      out.runs = runs;            // total runs incl. 1 penalty
      out.wideUnderlying = runs >= 2 ? wideUnderlying : null;
    } else if (kind === 'nb') {
      out.nbSource = nbSource;
      out.nbExtraRuns = nbSource === 'none' ? 0 : nbExtraRuns;
    } else if (kind === 'b' || kind === 'lb') {
      out.runs = runs;
    }
    onConfirm(out);
  }

  const titleMap = { wd: 'Wide', nb: 'No-ball', b: 'Bye', lb: 'Leg bye' };
  const titleColor = (kind === 'wd' || kind === 'nb') ? 'var(--sc-amber)' : 'var(--sc-text-mute)';

  return (
    <div className="sc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="sc-modal">
        <div className="sc-modal-body">
        <h3 style={{ color: titleColor }}>
          {titleMap[kind]}
          {kind === 'nb' && <span style={{ color: 'var(--sc-red)', fontSize: 11, fontWeight: 700, marginLeft: 8 }}>⚡ free hit next</span>}
        </h3>

        {/* Runs row */}
        {kind === 'wd' ? (
          <>
            <h4>How many runs total?</h4>
            <div className="sc-run-row">
              {[1,2,3,4,5].map(n => (
                <button
                  key={n}
                  className={`sc-run-chip ${runs === n ? 'sc-active' : ''}`}
                  onClick={() => setRuns(n)}
                >
                  {n}
                  {n === 1 && <span>just wide</span>}
                  {n === 2 && <span>+1 ran</span>}
                  {n === 3 && <span>+2 ran</span>}
                  {n === 4 && <span>+3 ran</span>}
                  {n === 5 && <span>boundary</span>}
                </button>
              ))}
            </div>
            {showWideUnderlying && (
              <>
                <h4>Extra runs were</h4>
                <div className="sc-chip-row">
                  <button className={`sc-chip ${wideUnderlying === 'extra' ? 'sc-active' : ''}`} onClick={() => setWUnder('extra')}>Extra running</button>
                  <button className={`sc-chip ${wideUnderlying === 'bye' ? 'sc-active' : ''}`} onClick={() => setWUnder('bye')}>Byes (past keeper)</button>
                  <button className={`sc-chip ${wideUnderlying === 'legbye' ? 'sc-active' : ''}`} onClick={() => setWUnder('legbye')}>Leg-byes (off pad)</button>
                </div>
              </>
            )}
          </>
        ) : kind === 'nb' ? (
          <>
            <h4>Runs come from?</h4>
            <div className="sc-source-row">
              <button className={`sc-source-chip ${nbSource === 'none' ? 'sc-active' : ''}`} onClick={() => setNbSource('none')}>
                <span className="sc-src-name">Just no-ball</span>
                <span className="sc-src-sub">(penalty only)</span>
              </button>
              <button className={`sc-source-chip ${nbSource === 'bat' ? 'sc-active' : ''}`} onClick={() => setNbSource('bat')}>
                <span className="sc-src-name">Off the bat</span>
                <span className="sc-src-sub">batter scored</span>
              </button>
              <button className={`sc-source-chip ${nbSource === 'bye' ? 'sc-active' : ''}`} onClick={() => setNbSource('bye')}>
                <span className="sc-src-name">Byes</span>
                <span className="sc-src-sub">missed, ran</span>
              </button>
              <button className={`sc-source-chip ${nbSource === 'legbye' ? 'sc-active' : ''}`} onClick={() => setNbSource('legbye')}>
                <span className="sc-src-name">Leg byes</span>
                <span className="sc-src-sub">off pad, ran</span>
              </button>
            </div>
            {nbSource !== 'none' && (
              <>
                <h4>How many additional runs?</h4>
                <div className="sc-run-row sc-cols-4">
                  {(nbSource === 'bat' ? [1,2,3,4,6] : [1,2,3,4]).map(n => (
                    <button
                      key={n}
                      className={`sc-run-chip ${nbExtraRuns === n ? 'sc-active' : ''}`}
                      onClick={() => setNbExtraRuns(n)}
                    >
                      {n}
                      {(n === 4 || n === 6) && <span>boundary</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <h4>How many runs?</h4>
            <div className="sc-run-row sc-cols-4">
              {[1,2,3,4].map(n => (
                <button
                  key={n}
                  className={`sc-run-chip ${runs === n ? 'sc-active' : ''}`}
                  onClick={() => setRuns(n)}
                >
                  {n}
                  {n === 4 && <span>boundary</span>}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Wicket question */}
        <h4>Wicket on this ball?</h4>
        <div className="sc-chip-row">
          {allowedWicketsByKind.includes('no') && (
            <button className={`sc-chip sc-danger ${wicketKind === 'no' ? 'sc-active' : ''}`} onClick={() => setWicketKind('no')}>No wicket</button>
          )}
          {allowedWicketsByKind.includes('stumped') && (
            <button className={`sc-chip sc-danger ${wicketKind === 'stumped' ? 'sc-active' : ''}`} onClick={() => setWicketKind('stumped')}>Stumped</button>
          )}
          {allowedWicketsByKind.includes('runout') && (
            <button className={`sc-chip sc-danger ${wicketKind === 'runout' ? 'sc-active' : ''}`} onClick={() => setWicketKind('runout')}>Run out</button>
          )}
        </div>

        <div className="sc-summary">
          <div className="sc-line"><strong>{summary()}</strong></div>
        </div>
        </div>{/* /sc-modal-body */}
        <div className="sc-modal-actions">
          <button className="sc-submit" onClick={confirm}>Confirm {titleMap[kind].toLowerCase()}</button>
          <button className="sc-cancel" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
